import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { AlertCircle, CheckCircle, ChevronDown, ChevronUp, Clock, Loader } from 'lucide-react';
import {
  fetchConversationDeliverySlotAvailability,
  updateConversationDeliverySlot,
} from './api';
import { DELIVERY_SLOT_OPTIONS, getDeliverySlotLabel } from '../../../utils/deliverySlots';

function compactError(error, fallback) {
  const payload = error?.response?.data;
  return payload?.message || payload?.error || error?.message || fallback;
}

export default function DeliverySlotInlineSelector({
  conversationId,
  conversation,
  onSlotUpdated,
}) {
  const [availability, setAvailability] = useState(null);
  const [selectedSlot, setSelectedSlot] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [isExpanded, setIsExpanded] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(false);

  const delivery = conversation?.delivery || {};
  const currentSlot = availability?.current_slot ?? delivery.slot ?? null;
  const currentSlotLabel = getDeliverySlotLabel(currentSlot) || 'Sin horario';
  const slotOptions = useMemo(() => (
    availability?.slot_options?.length
      ? availability.slot_options
      : DELIVERY_SLOT_OPTIONS.map((slot) => ({
          slot: slot.id,
          label: slot.compactLabel,
          available: false,
          disabled_reason: 'Cargando disponibilidad...',
        }))
  ), [availability]);

  const loadAvailability = useCallback(async () => {
    if (!conversationId) return;
    setIsLoading(true);
    setError(null);
    try {
      const data = await fetchConversationDeliverySlotAvailability(conversationId);
      setAvailability(data);
      setSelectedSlot(data.current_slot || null);
    } catch (err) {
      setError(compactError(err, 'No se pudo cargar la disponibilidad de horarios.'));
      setAvailability(null);
      setSelectedSlot(delivery.slot || null);
    } finally {
      setIsLoading(false);
    }
  }, [conversationId, delivery.slot]);

  useEffect(() => {
    setSuccess(false);
    setIsExpanded(false);
    loadAvailability();
  }, [loadAvailability]);

  async function handleSave() {
    if (!selectedSlot || isSaving || Number(selectedSlot) === Number(currentSlot)) return;
    setIsSaving(true);
    setError(null);
    setSuccess(false);
    try {
      const data = await updateConversationDeliverySlot(conversationId, selectedSlot);
      const nextAvailability = data.availability || null;
      setAvailability(nextAvailability);
      setSelectedSlot(data.delivery_point?.slot || selectedSlot);
      setSuccess(true);
      setIsExpanded(false);
      onSlotUpdated?.(data.delivery_point);
    } catch (err) {
      const payload = err?.response?.data;
      if (payload?.availability) setAvailability(payload.availability);
      setError(compactError(err, 'No se pudo guardar el horario.'));
    } finally {
      setIsSaving(false);
    }
  }

  const canUpdate = Boolean(availability?.can_update);
  const normalizedCurrentSlot = currentSlot ? Number(currentSlot) : null;
  const normalizedSelectedSlot = selectedSlot ? Number(selectedSlot) : null;
  const changed = normalizedSelectedSlot && normalizedSelectedSlot !== normalizedCurrentSlot;
  const toggleLabel = isExpanded
    ? 'Ocultar'
    : isLoading
      ? 'Cargando'
      : canUpdate
        ? 'Cambiar'
        : 'Ver reglas';

  return (
    <div className="border-b border-slate-200 bg-white px-3 py-2 sm:px-4">
      <button
        type="button"
        onClick={() => setIsExpanded(prev => !prev)}
        aria-expanded={isExpanded}
        className="flex w-full items-center justify-between gap-2 rounded-xl border border-slate-200 bg-slate-50 px-3 py-2 text-left transition hover:border-[#B1336E]/30 hover:bg-white"
      >
        <div className="flex min-w-0 flex-1 items-center gap-2">
          <Clock className="h-4 w-4 flex-shrink-0 text-[#B1336E]" />
          <div className="min-w-0">
            <div className="truncate text-sm font-extrabold text-slate-900">
              Horario de entrega
            </div>
            <p className="truncate text-xs text-slate-500">
              Actual: <span className="font-bold text-slate-700">{currentSlotLabel}</span>
              {delivery.district ? ` · ${delivery.district}` : ''}
            </p>
          </div>
        </div>
        <div className="flex flex-shrink-0 items-center gap-2">
          {success && !isExpanded && (
            <span className="hidden rounded-full bg-emerald-50 px-2 py-1 text-[11px] font-bold text-emerald-700 sm:inline">
              Actualizado
            </span>
          )}
          <span className="rounded-full bg-white px-2.5 py-1 text-[11px] font-extrabold text-[#B1336E] shadow-sm">
            {toggleLabel}
          </span>
          {isExpanded ? (
            <ChevronUp className="h-4 w-4 text-slate-400" />
          ) : (
            <ChevronDown className="h-4 w-4 text-slate-400" />
          )}
        </div>
      </button>

      {success && !isExpanded && (
        <div className="mt-2 flex items-center gap-2 rounded-lg border border-emerald-200 bg-emerald-50 px-3 py-2 text-xs font-semibold text-emerald-700">
          <CheckCircle className="h-4 w-4 flex-shrink-0" />
          Horario actualizado.
        </div>
      )}

      {error && !isExpanded && (
        <div className="mt-2 flex items-center gap-2 rounded-lg border border-red-200 bg-red-50 px-3 py-2 text-xs font-semibold text-red-700">
          <AlertCircle className="h-4 w-4 flex-shrink-0" />
          {error}
        </div>
      )}

      {isExpanded && (
        <div className="mt-3">
          <div className="mb-2 flex justify-end">
            <button
              type="button"
              onClick={loadAvailability}
              disabled={isLoading || isSaving}
              className="w-full rounded-lg bg-slate-100 px-3 py-2 text-xs font-bold text-slate-600 hover:bg-slate-200 disabled:opacity-50 sm:w-auto sm:py-1.5"
            >
              {isLoading ? 'Cargando...' : 'Actualizar disponibilidad'}
            </button>
          </div>

          {availability?.afternoon_only && (
            <div className="mb-2 rounded-lg border border-amber-200 bg-amber-50 px-3 py-2 text-xs font-semibold text-amber-800">
              Este distrito solo permite horarios desde las 4:00 pm.
            </div>
          )}

          {availability && !canUpdate && (
            <div className="mb-2 rounded-lg border border-slate-200 bg-slate-50 px-3 py-2 text-xs font-semibold text-slate-600">
              {availability.reason || 'Este horario no se puede editar.'}
            </div>
          )}

          {error && (
            <div className="mb-2 flex items-center gap-2 rounded-lg border border-red-200 bg-red-50 px-3 py-2 text-xs font-semibold text-red-700">
              <AlertCircle className="h-4 w-4 flex-shrink-0" />
              {error}
            </div>
          )}

          {success && (
            <div className="mb-2 flex items-center gap-2 rounded-lg border border-emerald-200 bg-emerald-50 px-3 py-2 text-xs font-semibold text-emerald-700">
              <CheckCircle className="h-4 w-4 flex-shrink-0" />
              Horario actualizado.
            </div>
          )}

          <div className="grid grid-cols-2 gap-2 sm:grid-cols-3 lg:grid-cols-6">
            {slotOptions.map((slot) => {
              const slotId = Number(slot.slot || slot.id);
              const isSelected = Number(selectedSlot) === slotId;
              const isCurrent = Number(currentSlot) === slotId;
              const disabled = isSaving || isLoading || !slot.available;
              return (
                <button
                  key={slotId}
                  type="button"
                  onClick={() => {
                    if (disabled) return;
                    setSelectedSlot(slotId);
                    setSuccess(false);
                    setError(null);
                  }}
                  disabled={disabled}
                  title={slot.disabled_reason || slot.label}
                  className={`min-h-[42px] rounded-lg border px-2 py-2 text-center text-xs font-extrabold transition disabled:cursor-not-allowed disabled:opacity-45 sm:px-3 ${
                    isSelected
                      ? 'border-[#B1336E] bg-[#B1336E] text-white'
                      : isCurrent
                        ? 'border-[#B1336E]/30 bg-[#B1336E]/10 text-[#B1336E]'
                        : 'border-slate-200 bg-slate-50 text-slate-700 hover:border-slate-300 hover:bg-white'
                  }`}
                >
                  {getDeliverySlotLabel(slotId, { compact: true }) || slot.label}
                </button>
              );
            })}
          </div>

          <div className="mt-3 flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
            <p className="text-[11px] leading-snug text-slate-500 sm:max-w-[70%]">
              Solo entregas pendientes. Cambios disponibles hasta la 1:00 pm del día de entrega.
            </p>
            <button
              type="button"
              onClick={handleSave}
              disabled={!canUpdate || !changed || isSaving || isLoading}
              className="inline-flex w-full items-center justify-center gap-1.5 rounded-lg bg-[#B1336E] px-3 py-2.5 text-xs font-bold text-white hover:bg-[#9d2d62] disabled:cursor-not-allowed disabled:opacity-50 sm:w-auto sm:py-2"
            >
              {isSaving ? <Loader className="h-3.5 w-3.5 animate-spin" /> : <CheckCircle className="h-3.5 w-3.5" />}
              Guardar horario
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
